<template>
    <el-card>
        <el-collapse v-model="collapseModel"
                     @change="handleChange">

            <el-collapse-item name="1">
                <template #title>
                    <div class="title-box">
                        <div class="img-container">
                            <span class="material-symbols-outlined">
                                equalizer
                            </span>
                        </div>
                        <span class="title-value">{{tittle}}</span>

                        <div v-if="limitReached"
                             class="limit-container">

                            <el-tooltip placement="top"
                                        :content="tooltipMessage">
                                <div class="img-container">
                                    <i class="limit-reached"></i>
                                </div>
                            </el-tooltip>

                        </div>
                    </div>


                </template>

                <el-descriptions>
                    <el-descriptions-item :label="$t('documentStatistics.documentsName')">
                        {{documentName}}
                    </el-descriptions-item>
                    <el-descriptions-item v-if="fileTypeLabel"
                                          :label="$t('documentStatistics.fileType')">
                        {{fileTypeLabel}}
                    </el-descriptions-item>
                    <el-descriptions-item v-if="documentInformation.pageCount && pageCountLabel"
                                          :label="pageCountLabel">
                        {{documentInformation.pageCount}}
                    </el-descriptions-item>
                    <el-descriptions-item v-if="documentInformation.wordCount"
                                          :label="$t('documentStatistics.wordCount')">
                        {{documentInformation.wordCount}}
                    </el-descriptions-item>
                    <el-descriptions-item v-if="segmentCount != null"
                                          :label="$t('documentStatistics.segmentCount')">
                        {{segmentCount}}
                    </el-descriptions-item>
                    <el-descriptions-item v-if="mergedSegmentCount"
                                          :label="$t('documentStatistics.mergedSegmentCount')">
                        {{mergedSegmentCount}}
                    </el-descriptions-item>
                    <el-descriptions-item v-if="createdAt"
                                          :label="$t('documentStatistics.createdAt')">
                        {{formatDate(createdAt)}}
                    </el-descriptions-item>
                    <el-descriptions-item v-if="updatedAt"
                                          :label="$t('documentStatistics.updatedAt')">
                        {{formatDate(updatedAt)}}
                    </el-descriptions-item>
                </el-descriptions>

                <div v-if="abbreviationsExist">
                    <div class="title">
                        {{$t('documentStatistics.abbreviationsUsed')}}
                    </div>
                    <div class="abbreviations-hint">
                        {{$t('documentStatistics.abbreviationsUsedHint')}}
                    </div>

                    <el-collapse v-model="abbreviationCollapseModel">
                        <el-collapse-item name="neverSplit">
                            <template #title>
                                {{neverSplitTitle}}
                            </template>
                            <div class="abbreviation-tags">
                                <el-tag v-for="entry in abbreviationSnapshot.neverSplit"
                                        :key="entry"
                                        disable-transitions>
                                    {{entry}}
                                </el-tag>
                            </div>
                        </el-collapse-item>

                        <el-collapse-item name="maySplit">
                            <template #title>
                                {{maySplitTitle}}
                            </template>
                            <div class="abbreviation-tags">
                                <el-tag v-for="entry in abbreviationSnapshot.maySplit"
                                        :key="entry"
                                        disable-transitions>
                                    {{entry}}
                                </el-tag>
                            </div>
                        </el-collapse-item>
                    </el-collapse>
                </div>

                <div v-if="limitReached">
                    <div class="title">
                        {{$t('documentStatistics.segmentsThatReachedLimit')}}
                    </div>
                    <el-table :data="tableData" stripe style="width:100%">
                        <el-table-column prop="segmentNumber" 
                                         width="180"
                                         :label="$t('documentStatistics.segmentNumber')" />
                        <el-table-column prop="startWith" 
                                         :label="$t('documentStatistics.segmentStartsWith')" />
                    </el-table>
                </div>

            </el-collapse-item>

        </el-collapse>
    </el-card>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { DocumentType } from '@/models/DocumentType'
    import { AbbreviationSnapshot } from '@/models/AbbreviationSettings'

    export default defineComponent({
        name: 'DocumentStatistics',
        props: {
            documentInformation: {
                type: Object,
                required: true,
            },
            documentName: {
                type: String,
                required: true,
            },
            documentType: {
                type: Number as PropType<DocumentType>,
                defaultValue: DocumentType.Docx
            },
            abbreviationSnapshot: {
                type: Object as PropType<AbbreviationSnapshot | null>,
                default: null,
            },
            createdAt: {
                type: String,
                default: '',
            },
            updatedAt: {
                type: String,
                default: '',
            },
            segmentCount: {
                type: Number as PropType<number | null>,
                default: null,
            },
            mergedSegmentCount: {
                type: Number as PropType<number | null>,
                default: null,
            },
        },
        data() {
            return {
                collapseModel: [] as string[],
                collapsedItems: [] as string[],
                abbreviationCollapseModel: [] as string[],

                tooltipMessage: this.$t('documentStatistics.fileContainSegmentsWithLimit'),
                tableData: [] as any,
                limitReached: false,

                documentStatistics: this.$t('documentStatistics.documentStatistics'),
                pageCount: this.$t('documentStatistics.pageCount'),
                sheetCount: this.$t('documentStatistics.sheetCount'),
                slideCount: this.$t('documentStatistics.slideCount'),
            };
        },
        mounted() {
            this.init();
        },
        computed: {
            tittle(): string {
                if (this.collapseModel.includes('1')) {
                    return this.documentStatistics;
                }

                return `${this.documentStatistics} '${this.documentName}'`;
            },
            pageCountLabel(): string {
                let label = ""

                switch (this.documentType) {
                    case DocumentType.Docx:
                        label = this.pageCount;
                        break;

                    case DocumentType.Xlsx:
                        label = this.sheetCount;
                        break;

                    case DocumentType.Pptx:
                        label = this.slideCount;
                        break;
                }
                return label;
            },
            fileTypeLabel(): string {
                switch (this.documentType) {
                    case DocumentType.Docx:
                        return 'DOCX';

                    case DocumentType.Xlsx:
                        return 'XLSX';

                    case DocumentType.Pptx:
                        return 'PPTX';

                    case DocumentType.Idml:
                        return 'IDML';
                }
                return '';
            },
            abbreviationsExist(): boolean {
                return this.abbreviationSnapshot != null;
            },
            neverSplitTitle(): string {
                return `${this.$t('documentStatistics.neverSplit')} (${this.abbreviationSnapshot?.neverSplit.length ?? 0})`;
            },
            maySplitTitle(): string {
                return `${this.$t('documentStatistics.maySplit')} (${this.abbreviationSnapshot?.maySplit.length ?? 0})`;
            },
        },
        watch: {
            tableData() {
                this.limitReached = this.tableData.length > 0;
            }
        },
        methods: {
            init() {
                this.tableData = this.documentInformation.invalidSegments
            },
            handleChange(val: string[]) {
                this.collapseModel = val;
            },
            formatDate(value: string): string {
                return new Date(value).toLocaleDateString();
            },
        },
    });

</script>

<style lang="scss" scoped>

    .title-box {
        color: var(--text-color);
        display: flex;
        font-family: Nunito;
    }

    .title-value {
        font-size: 19px;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }

    .limit-container {
        margin-left: 10px;
    }

    .limit-reached {
        background-image: url('@/images/alert.svg');
        width: 24px;
        height: 22px;
        background-repeat: no-repeat;
    }

    .title {
        display: flex;
        font-size: 16px;
        margin-top: 15px;
    }

    .abbreviations-hint {
        color: var(--el-text-color-secondary);
        font-size: 13px;
        margin-top: 4px;
    }

    .abbreviation-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }
</style>